import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { Cog, ServerCrash } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { api, ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'
import { EditGeneralSettingsSchemaForm } from '../schemas/EditGeneralSettingsSchema'

type PostEditGeneralSettingsProps = {
  friendlyUrlOrigin: string[]
  features: string
  toolsMenu: boolean
  quickAccess: boolean
  singleSignOn: boolean
  enableAccountEntity: boolean
}

async function postEditGeneralSettings(data: PostEditGeneralSettingsProps) {
  return api.backend.post('/general-configuration', data)
}

export function useEditGeneralSettings() {
  const { t } = useTranslation('settings')

  const { mutateAsync, isPending } = useMutation<
    unknown,
    AxiosError<ApiError>,
    PostEditGeneralSettingsProps
  >({
    mutationFn: postEditGeneralSettings,
    onSuccess: () => {
      toast(Toast, {
        data: {
          type: 'success',
          icon: Cog,
          title: t('general.use_edit_general_settings.toast_success.title'),
          description: t(
            'general.use_edit_general_settings.toast_success.description',
          ),
        },
      })
      queryClient.invalidateQueries({
        queryKey: ['active-general-settings'],
      })
    },
    onError: (error) => {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: ServerCrash,
          title: t('general.use_edit_general_settings.toast_error.title'),
          description:
            error?.response?.data?.friendlyMessage ||
            t('general.use_edit_general_settings.toast_error.description'),
        },
      })
    },
  })

  const handleEditGeneralSettings = async (
    data: EditGeneralSettingsSchemaForm,
  ) => {
    await mutateAsync({
      friendlyUrlOrigin: data.friendlyUrlOrigin.map((url) => url.value),
      features: JSON.parse(data.features),
      toolsMenu: data.toolsMenu,
      quickAccess: data.quickAccess,
      singleSignOn: data.singleSignOn,
      enableAccountEntity: data.enableAccountEntity,
    })
  }

  return {
    handleEditGeneralSettings,
    isEditingGeneralSettings: isPending,
  }
}
